import { type SupportedLocale } from '@zep-us/i18n';
import axios from 'axios';

import { CMS_CDN, isProduction } from '@/consts/env';

const WEEGLOO_SPACE_ID = process.env.NEXT_PUBLIC_WEEGLOO_SPACE_ID?.trim();
const WEEGLOO_CDN_TOKEN = process.env.NEXT_PUBLIC_WEEGLOO_CDN_TOKEN?.trim();

const assertWeeglooConfig = () => {
  if (
    [WEEGLOO_SPACE_ID, WEEGLOO_CDN_TOKEN].some(
      value => !value || value === 'undefined' || value === 'null',
    )
  ) {
    throw new Error('Missing required Weegloo environment variables');
  }
};

/**
 * Weegloo CDA HTTP Instance (읽기 전용)
 * @description Content Delivery API - 퍼블리시된 콘텐츠 조회용
 * @see https://zep.us/weegloo/cda/v1/spaces/{SPACE_ID}/contents/
 */
const WEEGLOO_CDA_INSTANCE = axios.create({
  baseURL: `https://zep.us/weegloo/cda/v1/spaces/${WEEGLOO_SPACE_ID}/contents/`,
  headers: {
    Authorization: `Bearer ${WEEGLOO_CDN_TOKEN}`,
  },
  // 비필수 콘텐츠 전용. weegloo/CDA 가 간헐적으로 길게 hang 하는데 timeout 미설정이라
  // 무한 대기했다. 8s 로 끊고 react-query 재시도로 회수한다(공유 CMS 인스턴스와 동일 기준, WEB-APP-1QJ).
  timeout: 8000,
});

/**
 * CDA API 응답 타입 (locale-unwrapped 형태)
 * CDA는 쿼리 파라미터로 locale을 받아서 해당 locale 데이터만 직접 반환합니다.
 */
type WeeglooContentCDAResponse<T = object> = {
  metadata: { tags: [] };
  fields: {
    json: T;
    jsonDev: T;
    title: string;
  };
  sys: {
    version: number;
  };
};

/**
 * Weegloo CDA에서 퍼블리시된 콘텐츠를 조회합니다.
 * @description Content Delivery API 사용 (읽기 전용)
 * @param entryId 요청할 Weegloo Content ID
 * @param locale 조회할 locale (optional, 기본값: en-US)
 * @returns {Promise<T>} 바로 데이터 반환
 */
export const getZepetofulCDNContent = async <T = object>({
  entryId,
  locale = 'en-US',
}: {
  entryId: string;
  locale?: SupportedLocale | 'en-US';
}): Promise<T> => {
  assertWeeglooConfig();

  const res = await WEEGLOO_CDA_INSTANCE.get<WeeglooContentCDAResponse<T>>(
    entryId,
    {
      params: {
        locale: locale === 'en' ? 'en-US' : locale,
        select: isProduction ? 'fields.json' : 'fields.jsonDev',
      },
    },
  );
  const jsonData = res.data.fields.json ?? res.data.fields.jsonDev;
  return replaceWeeglooCdnUrl(jsonData) as T;
};

/**
 * ⚠️ `libs/service/apis/src/zepetoful/utils.ts` 의 동명 함수와 **동작이 같아야 한다.**
 * quiz-app 은 `@zep/apis` 에 의존하지 않아 복제를 유지한다(위 `CMS_CDN` 주석 참조).
 * **한쪽 로직을 바꾸면 반드시 다른 쪽도 바꿀 것.**
 *
 * CMS 콘텐츠에 아직 위글루 절대 URL 이 남아 있어 이 치환이 필요하다. 에셋 실물은
 * `s3://zep-content-management-system/cms/media/` 로 이관 완료(802건)됐다.
 */
export const replaceWeeglooCdnUrl = (data: unknown): unknown => {
  if (typeof data === 'string') {
    return data.replace(
      /https:\/\/(?:files\.weegloo\.com|weegloo-media\.com)/g,
      CMS_CDN,
    );
  }

  if (Array.isArray(data)) {
    return data.map(item => replaceWeeglooCdnUrl(item));
  }

  if (data && typeof data === 'object') {
    return Object.fromEntries(
      Object.entries(data).map(([key, value]) => [
        key,
        replaceWeeglooCdnUrl(value),
      ]),
    );
  }

  return data;
};
